"use client";

import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { FormField } from "../ui/form-field";
import { getServiceCity } from "redux/actions/UserServiceCity";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";

const validate = (field, value) => {
  switch (field) {
    case "name":
      if (!value || !value.trim()) return "Zone name is required";
      return "";
    case "locationId":
      if (!value) return "City is required";
      return "";
    case "lat": {
      if (value === "" || value === null || value === undefined) return "Latitude is required";
      const num = parseFloat(value);
      if (isNaN(num)) return "Latitude must be a valid number";
      if (num < -90 || num > 90) return "Latitude must be between -90 and 90";
      return "";
    }
    case "lng": {
      if (value === "" || value === null || value === undefined) return "Longitude is required";
      const num = parseFloat(value);
      if (isNaN(num)) return "Longitude must be a valid number";
      if (num < -180 || num > 180) return "Longitude must be between -180 and 180";
      return "";
    }
    case "radiusKm": {
      if (value === "" || value === null || value === undefined) return "Radius is required";
      const num = parseFloat(value);
      if (isNaN(num)) return "Radius must be a valid number";
      if (num <= 0) return "Radius must be greater than 0";
      return "";
    }
    default:
      return "";
  }
};

export const validateZoneForm = (formData) => {
  const fields = ["name", "locationId", "lat", "lng", "radiusKm"];
  return fields.every((f) => validate(f, formData[f]) === "");
};

export default function AddServiceableZoneForm({
  formData,
  onFormDataChange,
}) {
  const dispatch = useDispatch();
  const cityData = useSelector(
    (state) => state?.userServiceCity?.userServiceCity?.data
  );
  const cityOptions =
    cityData?.length > 0
      ? cityData.map((val) => ({ name: val?.name, id: val?.id }))
      : [];

  const [touched, setTouched] = useState({});

  const updateFormData = (field, value) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    onFormDataChange({ ...formData, [field]: value });
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const err = (field) =>
    touched[field] ? validate(field, formData[field]) : "";

  useEffect(() => {
    dispatch(getServiceCity());
  }, [dispatch]);

  return (
    <>
      <FormField label="Zone Name" required htmlFor="zoneName">
        <Input
          id="zoneName"
          placeholder="Enter zone name"
          value={formData.name}
          onChange={(e) => updateFormData("name", e.target.value)}
          onBlur={() => handleBlur("name")}
          className={`h-12 text-base bg-white ${
            err("name") ? "border-red-500 focus:ring-red-500" : ""
          }`}
        />
        {err("name") && (
          <p className="text-xs text-red-500 mt-1">{err("name")}</p>
        )}
      </FormField>

      <FormField label="City" required>
        <Select
          value={formData.locationId}
          onValueChange={(value) => updateFormData("locationId", value)}
        >
          <SelectTrigger
            className={`h-12 text-base bg-white border ${
              err("locationId") ? "border-red-500" : "border-gray-300"
            }`}
            onBlur={() => handleBlur("locationId")}
          >
            <SelectValue placeholder="Choose city" />
          </SelectTrigger>
          <SelectContent className="bg-white border border-gray-200 shadow-lg z-50">
            {cityOptions.map((city) => (
              <SelectItem
                key={city.id}
                value={city.id}
                className="text-base bg-white hover:bg-gray-50 focus:bg-gray-50 cursor-pointer"
              >
                {city.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {err("locationId") && (
          <p className="text-xs text-red-500 mt-1">{err("locationId")}</p>
        )}
      </FormField>

      <FormField label="Latitude" required htmlFor="lat">
        <Input
          id="lat"
          type="number"
          placeholder="Enter latitude (e.g. 12.9716)"
          value={formData.lat}
          onChange={(e) => updateFormData("lat", e.target.value)}
          onBlur={() => handleBlur("lat")}
          className={`h-12 text-base bg-white ${
            err("lat") ? "border-red-500 focus:ring-red-500" : ""
          }`}
          onWheel={(e) => e.target.blur()}
        />
        {err("lat") && (
          <p className="text-xs text-red-500 mt-1">{err("lat")}</p>
        )}
      </FormField>

      <FormField label="Longitude" required htmlFor="lng">
        <Input
          id="lng"
          type="number"
          placeholder="Enter longitude (e.g. 77.5946)"
          value={formData.lng}
          onChange={(e) => updateFormData("lng", e.target.value)}
          onBlur={() => handleBlur("lng")}
          className={`h-12 text-base bg-white ${
            err("lng") ? "border-red-500 focus:ring-red-500" : ""
          }`}
          onWheel={(e) => e.target.blur()}
        />
        {err("lng") && (
          <p className="text-xs text-red-500 mt-1">{err("lng")}</p>
        )}
      </FormField>

      <FormField label="Radius (Km)" required htmlFor="radiusKm">
        <Input
          id="radiusKm"
          type="number"
          placeholder="Enter radius in km"
          value={formData.radiusKm}
          onChange={(e) => updateFormData("radiusKm", e.target.value)}
          onBlur={() => handleBlur("radiusKm")}
          className={`h-12 text-base bg-white ${
            err("radiusKm") ? "border-red-500 focus:ring-red-500" : ""
          }`}
          onWheel={(e) => e.target.blur()}
          min="0.01"
          step="0.01"
        />
        {err("radiusKm") && (
          <p className="text-xs text-red-500 mt-1">{err("radiusKm")}</p>
        )}
      </FormField>
    </>
  );
}
